import {
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  EventEmitter,
  input,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { NavigationExtras, Router } from '@angular/router';
import {
  ActionSheetController,
  IonItemSliding,
  ModalController,
  Platform,
} from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { catchError, filter, map, of, take } from 'rxjs';

import {
  AttendeeInformation,
  CalendarEvent,
  DATETIMEFORMART,
  EventRequest,
  ForwardEventRequest,
  UnleashFeatureFlags,
} from 'src/app/interfaces/interfaces';
import { ApiService } from 'src/app/services/api-services/api.service';
import { LanguageService } from 'src/app/services/languageservice/language.service';
import { checkIsNextDay } from 'src/app/utils/utils';
import * as moment from 'moment';
import { StringInfoMessageService } from 'src/app/services/string-info-message/string-info-message.service';
import { DatetimeFormatService } from 'src/app/services/datetime-format-service/datetime-format.service';
import { PlacesService } from 'src/app/services/placesService/places.service';
import { FloorplanSearchService } from '../search/floorplan-search.service';
import { EventComponent } from '../event/event.component';
import { ViewEditBookingComponent } from '../view-edit-booking/view-edit-booking.component';
import { EVENTPAGETYPES } from '../place/place.component';
import { EventDescriptionModalComponent } from '../event-description-modal/event-description-modal.component';
import { AddAttendeeComponent } from '../add-attendee/add-attendee.component';
import { Capacitor } from '@capacitor/core';
import FileSave from 'custom-plugins/android/file-save';
import { BookingsStore } from 'src/app/stores/bookings.store';
import { UserStore } from 'src/app/stores/user.store';
import { AttendeesStore } from 'src/app/stores/attendees.store';
import { ToastService } from 'src/app/services/toast-service/toast.service';
import { AddResourceComponent } from '../add-resource/add-resource.component';
import { UnleashService } from 'src/app/services/unleash/unleash.service';
import { FileSharer } from '@capgo/capacitor-file-sharer';
export type InteractionMode = 'full' | 'readOnly'; 
@Component({
  selector: 'app-my-bookings-item-legacy',
  templateUrl: './my-bookings-item-legacy.component.html',
  styleUrls: ['./my-bookings-item-legacy.component.scss'],
})
export class MyBookingsItemLegacyComponent implements OnDestroy {
  calendarEvent = input<any>();

  @Input() selectedSegment;
  @Input() interactionMode: InteractionMode = 'full';

  showResourceImages = false;
  @ViewChild('resourceImagesPopover') resourceImagesPopover;
  @ViewChild('popover') popover;
  @ViewChild('slidingItem') slidingItem: IonItemSliding;
  @ViewChild('SeriesPopOver') SeriesPopOver;
  @ViewChild('ResponsePopOver') ResponsePopOver;
  @ViewChild('userResponseTooltipPopover') userResponseTooltipPopover;
  unleashFeatureFlags = UnleashFeatureFlags;

  public locale = this.dateTimeFormatService.selectedDateTimeFormat;
  public readonly meetingEndingSoonThreshold = 15;
  public isExtendMeetingPopoverOpen = false;
  isLoading: boolean = false;
  // myResponseToEvent: string;
  selectedContentSegment: string = 'text';
  // isSeries: boolean = false;
  noAddressSpecified: boolean = false;
  address = computed(() =>
    this.stringInfoMessageService.buildAddressString(this.calendarEvent().place)
  );
  isNextDay = computed(() =>
    checkIsNextDay(this.calendarEvent().startTime, this.calendarEvent().endTime)
  );
  extendedAttendees = computed(() =>
    this.attendeesStore.extendAttendeeInformation(
      this.calendarEvent().attendees
    )
  );

  userResponse = computed(() => {
    return this.extendedAttendees().find(
      (attendee) => attendee.userPrincipalName == this.userStore.user()?.mail
    )?.response;
  });

  acceptedAttendees = computed(() =>
    this.extendedAttendees().filter(
      (attendee) => attendee.response.toLowerCase() === 'accepted'
    )
  );
  declinedAttendees = computed(() =>
    this.extendedAttendees().filter(
      (attendee) => attendee.response.toLowerCase() === 'declined'
    )
  );
  pendingAttendees = computed(() =>
    this.extendedAttendees().filter((attendee) => {
      const response = attendee.response.toLowerCase();
      return (
        (response === 'none' || response === 'notresponded') &&
        attendee.userPrincipalName !==
          this.calendarEvent().organizer.emailAddress
      );
    })
  );
  tentativelyAttendees = computed(() =>
    this.extendedAttendees().filter(
      (attendee) => attendee.response.toLowerCase() === 'tentativelyaccepted'
    )
  );
  subjectFirstSegment = computed(() => {
    if (this.calendarEvent().isCancelled) {
      const subject = this.calendarEvent().subject as string;
      if (subject.includes('Canceled') || subject.includes('Abgesagt')) {
        const index = subject.indexOf(':');
        const subjectSplitted =
          index !== -1
            ? [subject.substring(0, index), subject.substring(index + 1)]
            : [subject];
        return subjectSplitted[0] + ':';
      }
    }
    return null;
  });
  subjectSecondSegment = computed(() => {
    if (this.calendarEvent().isCancelled) {
      const subject = this.calendarEvent().subject as string;
      if (subject.includes('Canceled') || subject.includes('Abgesagt')) {
        const index = subject.indexOf(':');
        const subjectSplitted =
          index !== -1
            ? [subject.substring(0, index), subject.substring(index + 1)]
            : [subject];
        return subjectSplitted[1] && subjectSplitted[1].trim() !== ''
          ? subjectSplitted[1]
          : null;
      }
    }
    return null;
  });
  isMeetingEndingSoon = computed(() => {
    if (this.calendarEvent().resourceStatus !== 'Accepted') return false;
    const now = moment();
    const startTime = moment(this.calendarEvent().startTime);
    const endTime = moment(this.calendarEvent().endTime);
    if (now.isAfter(startTime)) {
      const minutes = endTime.diff(now, 'minutes') + 1; // because the different is not including the current minute
      return minutes <= this.meetingEndingSoonThreshold && minutes > 0 && this.calendarEvent().isOrganizer;
    }
    return false;
  });
  minutesRemaining = computed(() => {
    if (this.calendarEvent().resourceStatus !== 'Accepted') return 0;
    const now = moment();
    const startTime = moment(this.calendarEvent().startTime);
    const endTime = moment(this.calendarEvent().endTime);
    if (now.isAfter(startTime)) {
      return endTime.diff(now, 'minutes') + 1;
    }
    return 0;
  });
  checkInEnabled = computed(() => {
    const event = this.calendarEvent();
    
    const eventStart = new Date(event.startTime).getTime();
    const now = Date.now();
    const diffMinutes = (eventStart - now) / 60000;

    const eventAlreadyStarted = now >= eventStart;
    const preCheckInAllowed =
      event.place?.preCheckInEnabled &&
      diffMinutes <= event.place.preCheckInTime;
    const durationRemainingLessThanOneMinute = (new Date(event.endTime).getTime() - now) / 60000 <= 1;
    
    return (
      !this.isReadOnly &&
      event.resourceStatus == 'Accepted' &&
      !event.isCancelled &&
      !event.checkedIn &&
      !event.place?.checkOutOnlyEnabled &&
      !durationRemainingLessThanOneMinute &&
      (eventAlreadyStarted || preCheckInAllowed)
    );
  });
  checkOutEnabled = computed(() => {
    const event = this.calendarEvent();
    
    const now = Date.now();
    const durationRemainingLessThanOneMinute = (new Date(event.endTime).getTime() - now) / 60000 <= 1;
    
    return (
      this.calendarEvent().isOrganizer &&
      event.resourceStatus == 'Accepted' &&
      !event.isCancelled &&
      event.checkedIn &&
      !durationRemainingLessThanOneMinute &&
      !this.isCheckedOut
    );
  });
  attachmentsLoading: boolean = false;
  extendMeetingAvailibility: any = {
    '15': false,
    '30': false,
    '45': false,
    '60': false,
  };
  isMobile: boolean = false;
  extendMeetingAvailibilityError: string = '';
  extendMeetingAvailibilityLoading: boolean = false;
  isDescriptionExpanded: boolean = false;
  isCheckedOut: boolean = false;
  constructor(
    public platform: Platform,
    private languageService: LanguageService,
    private stringInfoMessageService: StringInfoMessageService,
    private router: Router,
    public actionSheetController: ActionSheetController,
    private translateService: TranslateService,
    private apiService: ApiService,
    private cgf: ChangeDetectorRef,
    private dateTimeFormatService: DatetimeFormatService,
    private mdlCtrl: ModalController,
    private floorplanSearchService: FloorplanSearchService,
    private bookingsStore: BookingsStore,
    private userStore: UserStore,
    private attendeesStore: AttendeesStore,
    private toast: ToastService,
    private modalController: ModalController,
    private toastService: ToastService,
     public unleashService: UnleashService,
  ) {
    this.isMobile = this.platform.is('mobile') && !this.platform.is('tablet');

    effect(() => {
      this.calendarEvent(); 
      // reset sliding item state on calendar event updates to prevent blank card on subsequent swipe interactions
      this.slidingItem?.closeOpened();
    });
  }
  ngOnDestroy(): void {}

  async acceptEvent(eventId, isSeriesAction = false) {
    this.slidingItem.closeOpened();
    const actionSheet = await this.actionSheetController.create({
      header: isSeriesAction
        ? this.translateService.instant('acceptSeriesMeetingQuestion')
        : this.translateService.instant('acceptMeetingQuestion'),
      mode: 'ios',
      buttons: [
        {
          text: this.translateService.instant('feedback.answers.yes'),
          role: 'destructive',
        },
        {
          text: this.translateService.instant('feedback.answers.no'),
          role: 'cancel',
        },
      ],
    });
    await actionSheet.present();
    const condition = await actionSheet.onDidDismiss();
    if (condition.role === 'destructive') {
      this.bookingsStore.acceptInvitation(eventId, isSeriesAction).subscribe({
        next: () => {},
        error: (ex) => {
          this.toast.openErrorToastAsync(ex.error.message);
        },
      });
    }
  }

  async declineEvent(eventId, isSeriesAction = false) {
    this.slidingItem.closeOpened();
    const actionSheet = await this.actionSheetController.create({
      header: isSeriesAction
        ? this.translateService.instant('declineSeriesMeetingQuestion')
        : this.translateService.instant('declineMeetingQuestion'),
      mode: 'ios',
      buttons: [
        {
          text: this.translateService.instant('feedback.answers.yes'),
          role: 'destructive',
        },
        {
          text: this.translateService.instant('feedback.answers.no'),
          role: 'cancel',
        },
      ],
    });
    await actionSheet.present();
    const condition = await actionSheet.onDidDismiss();
    if (condition.role === 'destructive') {
      this.bookingsStore.declineInvitation(eventId, isSeriesAction).subscribe({
        next: () => {},
        error: (ex) => {
          this.toast.openErrorToastAsync(ex.error.message);
        },
      });
    }
  }

  async tentativelyAcceptEvent(eventId, isSeriesAction = false) {
    this.slidingItem.closeOpened();
    const actionSheet = await this.actionSheetController.create({
      header: isSeriesAction
        ? this.translateService.instant(
            'tentativelyAcceptSeriesMeetingQuestion'
          )
        : this.translateService.instant('tentativelyAcceptMeetingQuestion'),
      mode: 'ios',
      buttons: [
        {
          text: this.translateService.instant('feedback.answers.yes'),
          role: 'destructive',
        },
        {
          text: this.translateService.instant('feedback.answers.no'),
          role: 'cancel',
        },
      ],
    });
    await actionSheet.present();
    const condition = await actionSheet.onDidDismiss();
    if (condition.role === 'destructive') {
      this.bookingsStore
        .tentativelyAcceptInvitation(eventId, isSeriesAction)
        .subscribe({
          next: () => {},
          error: (ex) => {
            this.toast.openErrorToastAsync(ex.error.message);
          },
        });
    }
  }

  async showEditBookingModal() {
    const event = this.calendarEvent();
    if (this.unleashService.isEnabled(UnleashFeatureFlags.BOOKING_CHANGE_DESIGN)) {
      const modal: HTMLIonModalElement = await this.mdlCtrl.create({
        component: ViewEditBookingComponent,
        initialBreakpoint: 0.95,
        breakpoints: [0, 0.95],
        handle: false,
        canDismiss: async (data, role) => role != 'gesture',
        componentProps: {
          attendees: event.attendees,
          place: event.place,
          meetingToEdit: event,
        },
      });
      await modal.present();
      const { data } = await modal.onDidDismiss();
      if (data?.action === 'declined' && data?.eventId) {
        this.bookingsStore.getMyBookings();
      }
    } else {
      const modal: HTMLIonModalElement = await this.mdlCtrl.create({
        component: EventComponent,
        initialBreakpoint: 0.95,
        breakpoints: [0, 0.95],
        handle: false,
        canDismiss: async (data, role) => role != 'gesture',
        componentProps: {
          finalChoosenAttendees: this.extendedAttendees(),
          place: event.place,
          purpose: 'edit',
          meetingToEdit: event,
        },
      });
      modal.present();
    }
  }

  async showReadOnlyBookingModal() {
    const event = this.calendarEvent();
    if (this.unleashService.isEnabled(UnleashFeatureFlags.BOOKING_CHANGE_DESIGN)) {
      const modal: HTMLIonModalElement = await this.mdlCtrl.create({
        component: ViewEditBookingComponent,
        initialBreakpoint: 0.95,
        breakpoints: [0, 0.95],
        handle: false,
        canDismiss: async (data, role) => role != 'gesture',
        componentProps: {
          attendees: event.attendees,
          place: event.place,
          meetingToEdit: event,
        },
      });
      await modal.present();
      const { data } = await modal.onDidDismiss();
      if (data?.action === 'declined' && data?.eventId) {
        this.bookingsStore.getMyBookings();
      }
    } else {
      const modal: HTMLIonModalElement = await this.mdlCtrl.create({
        component: EventComponent,
        initialBreakpoint: 0.95,
        breakpoints: [0, 0.95],
        handle: false,
        canDismiss: async (data, role) => role != 'gesture',
        componentProps: {
          finalChoosenAttendees: event.attendees,
          place: event.place,
          purpose: 'readOnlyBooking',
          meetingToEdit: event,
        },
      });
      await modal.present();
      const { data } = await modal.onDidDismiss();
      if (data?.action === 'declined' && data?.eventId) {
        this.bookingsStore.getMyBookings();
      }
    }
  }

  async doDeleteBooking(id: string, isSeriesAction: boolean = false) {
    const actionSheet = await this.actionSheetController.create({
      header: isSeriesAction
        ? this.translateService.instant('deleteSeriesBookingQuestion')
        : this.translateService.instant('deleteBookingQuestion'),
      mode: 'ios',
      buttons: [
        {
          text: isSeriesAction
            ? this.translateService.instant('deleteSeriesBooking')
            : this.translateService.instant('deleteBooking'),
          role: 'destructive',
        },
        {
          text: this.translateService.instant('cancelButton'),
          role: 'cancel',
        },
      ],
    });
    await actionSheet.present();
    const condition = await actionSheet.onDidDismiss();
    if (condition.role === 'destructive') {
      this.bookingsStore.deleteBooking(id, isSeriesAction).subscribe({
        next: () => {},
        error: (ex) => {
          this.toast.openErrorToastAsync(ex.error.message);
        },
      });
    }
  }


  buildSeriesInformation(recurrencePattern) {
    return this.stringInfoMessageService.seriesEventInformationMessage(
      recurrencePattern,
      true
    );
  }

  async chooseIfSeriesToast(type) {
    this.slidingItem.closeOpened();
    let actionSheetButtons = [
      {
        text: this.translateService.instant('series.EventOccurrence'),
        role: 'occurrence',
      },
      {
        text: this.translateService.instant('series.EventSeries'),
        role: 'series',
      },
      {
        text: this.translateService.instant('cancelButton'),
        role: 'cancel',
      },
    ];

    let actionSheetHeader = this.translateService.instant(
      'series.' + type + 'SeriesEventHeaderModal'
    );

    const actionSheet = await this.actionSheetController.create({
      header: actionSheetHeader,
      mode: 'ios',
      buttons: actionSheetButtons,
    });
    await actionSheet.present();
    const condition = await actionSheet.onDidDismiss();
    this.chooseMatchingAction(type, condition.role);
  }
  chooseMatchingAction(type, role) {
    switch (type) {
      case 'delete':
        if (role === 'occurrence') {
          this.doDeleteBooking(this.calendarEvent().id, false);
        }
        if (role === 'series') {
          this.doDeleteBooking(this.calendarEvent().seriesMasterId, true);
        }
        break;
      case 'accept':
        if (role === 'occurrence') {
          this.acceptEvent(this.calendarEvent().id, false);
        }
        if (role === 'series') {
          this.acceptEvent(this.calendarEvent().seriesMasterId, true);
        }
        break;
      case 'tentativelyAccept':
        if (role === 'occurrence') {
          this.tentativelyAcceptEvent(this.calendarEvent().id, false);
        }
        if (role === 'series') {
          this.tentativelyAcceptEvent(
            this.calendarEvent().seriesMasterId,
            true
          );
        }
        break;
      case 'decline':
        if (role === 'occurrence') {
          this.declineEvent(this.calendarEvent().id, false);
        }
        if (role === 'series') {
          this.declineEvent(this.calendarEvent().seriesMasterId, true);
        }
        break;
      case 'edit':
        if (role === 'occurrence') {
          this.calendarEvent().editMasterEvent = false;
          this.showEditBookingModal();
        }
        if (role === 'series') {
          this.calendarEvent().editMasterEvent = true;
          this.showEditBookingModal();
        }
        break;
      default:
        break;
    }
  }

  joinMeeting(url: string) {
    window.open(url, '_blank');
  }

  openMap(place) {
    this.floorplanSearchService.openMap(place);
  }

  toggleExtendMeetingPopover() {
    this.isExtendMeetingPopoverOpen = !this.isExtendMeetingPopoverOpen;
    if (this.isExtendMeetingPopoverOpen) {
      this.extendMeetingAvailibilityLoading = true;
      this.extendMeetingAvailibilityError = '';
      if (!this.calendarEvent().place) {
        this.extendMeetingAvailibility = {
          '15': true,
          '30': true,
          '45': true,
          '60': true,
        };
        return;
      }
      const startTime = moment(this.calendarEvent().endTime)
        .add(1, 'minutes')
        .utc()
        .format(DATETIMEFORMART);
      const endTime = moment(this.calendarEvent().endTime)
        .add(1, 'hours')
        .utc()
        .format(DATETIMEFORMART);
      this.apiService
        .getAvailibilityToExtendMeeting(
          this.calendarEvent().place?.emailAddress,
          startTime,
          endTime,
          this.calendarEvent().place?.isOnPrem
        )
        .subscribe({
          next: (res) => {
            this.extendMeetingAvailibility = res;

            if (!res) {
              this.extendMeetingAvailibilityError =
                this.translateService.instant('error fetching availability');
            } else if (Object.values(res).every((value) => value === false)) {
              this.extendMeetingAvailibilityError =
                this.translateService.instant('slotsBooked');
            } else {
              this.extendMeetingAvailibilityError = null;
            }

            this.extendMeetingAvailibilityLoading = false;
          },
          error: (err) => {
            this.toast.openErrorToastAsync(err.message);
            this.extendMeetingAvailibilityLoading = false;
          },
        });
    }
  }

  extendMeeting(minutes: number) {
    this.extendMeetingAvailibilityLoading = true;
    const newEndTime = moment(this.calendarEvent().endTime)
      .add(minutes, 'minutes')
      .format(DATETIMEFORMART);

    try {
      const eventRequest: EventRequest =
        this.mapCalendarEventIntoEventRequest(newEndTime);
      this.bookingsStore.editBooking(this.calendarEvent().id, false, eventRequest)
        .pipe(
          catchError((error) => {
            this.toast.openErrorToastAsync(error.error.message);
            return of(false);
          })
        )
        .subscribe((res) => {
          this.isExtendMeetingPopoverOpen = false;
          this.extendMeetingAvailibilityLoading = false;
        });
    } catch (error) {
      console.error('Failed to extend meeting:', error);
      this.extendMeetingAvailibilityError = this.translateService.instant(
        'error extending meeting'
      );
      this.extendMeetingAvailibilityLoading = false;
    }
  }

  private mapCalendarEventIntoEventRequest(newEndTime: string = null): EventRequest {
    const recurrancePattern =
      this.calendarEvent().recurrencePattern?.accuranceType == null
        ? null
        : this.calendarEvent().recurrencePattern;
    return {
      showAs: this.calendarEvent().showAs,
      subject: this.calendarEvent().subject,
      startTime: moment(this.calendarEvent().startTime).format(DATETIMEFORMART),
      endTime: newEndTime ?? moment(this.calendarEvent().endTime).format(DATETIMEFORMART),
      isOnPrem: this.calendarEvent().place?.isOnPrem,
      attendees: this.extendedAttendees(),
      placeEmailAddress: this.calendarEvent().place?.emailAddress,
      recurrencePattern: recurrancePattern,
      reminderMinutesBeforeStart:
        this.calendarEvent().reminderMinutesBeforeStart,
      isOnlineMeeting: this.calendarEvent().isOnlineMeeting,
      timeZoneInfo: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
  }

  toggleImageContainer(event?) {
    this.showResourceImages = !this.showResourceImages;
    if (this.resourceImagesPopover) {
      this.resourceImagesPopover.event = event;
    }
  }

  toggleDescription() {
    this.isDescriptionExpanded = !this.isDescriptionExpanded;
    if (
      this.isDescriptionExpanded &&
      !this.calendarEvent().attachments &&
      this.calendarEvent().hasAttachments
    ) {
      this.getEventAttachment();
    }
  }

  private getEventAttachment() {
    this.attachmentsLoading = true;
    this.apiService.getEventAttachment(this.calendarEvent().id).subscribe({
      next: (res) => {
        res.forEach((att) => {
          if (att.isInline && att.contentId && att.contentBytes) {
            const regex = new RegExp(`cid:${att.contentId}`, 'g');
            const base64Image = `data:${att.contentType};base64,${att.contentBytes}`;
            this.calendarEvent().description =
              this.calendarEvent().description.replace(regex, base64Image);
          }
          if (!att.isInline) {
            att.size = this.formatFileSize(att.size);
          }
        });
        this.calendarEvent().attachments = res.filter((att) => !att.isInline);
        this.attachmentsLoading = false;
      },
      error: (error) => {
        this.toast.openErrorToastAsync(error.message);
        this.attachmentsLoading = false;
      },
    });
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  async downloadAttachment(attachment: any) {
    this.attachmentsLoading = true;
    try {
      const platform = Capacitor.getPlatform();
      const fileName = attachment.name;

      this.apiService
        .getEventAttachmentFile(this.calendarEvent().id, attachment.id)
        .subscribe({
          next: (blob: Blob) => {
            this.shareFile(blob, fileName, attachment.contentType);
          },
          error: (error) => {
            console.error('Error downloading attachment:', error);
            this.toast.openErrorToastAsync(error.message);
          },
          complete: () => {
            this.attachmentsLoading = false;
          },
        });
    } catch (error) {
      console.error('Error downloading attachment:', error);
      this.attachmentsLoading = false;
    }
  }

  private async shareFile(blob: Blob, fileName: string, contentType: string) {
    try {
      const platform = Capacitor.getPlatform();

      // Convert blob to base64
      const reader = new FileReader();
      reader.onload = async () => {
        const base64Data = (reader.result as string).split(',')[1];

        if (platform === 'android') {
          // Use custom FileSave plugin for Android
          try {
            const result = await FileSave.saveFile({
              filename: fileName,
              mimeType: contentType,
              base64Data: base64Data,
            });

            if (result.uri) {
              console.log('File saved successfully at:', result.uri);
              // You can add a success toast here if needed
            }
          } catch (error) {
            console.error('Error saving file on Android:', error);
            // You can add an error toast here if needed
          }
        } else {
          // Use FileSharer for other platforms (iOS, web, etc.)
          try {
            await FileSharer.share({
              filename: fileName,
              contentType: contentType,
              base64Data: base64Data,
            });
          } catch (error) {
            console.error('Error sharing file:', error);
            // You can add an error toast here if needed
          }
        }
      };
      reader.readAsDataURL(blob);
    } catch (error) {
      console.error('Error sharing file on mobile:', error);
    }
  }

  async openDescriptionModal() {
    const modal = await this.mdlCtrl.create({
      component: EventDescriptionModalComponent,
      componentProps: {
        calendarEvent: this.calendarEvent(),
        selectedContentSegment: this.selectedContentSegment,
        title: this.calendarEvent().subject,
      },
      canDismiss: async (data, role) => role != 'gesture',
      breakpoints: [0, 0.95],
      initialBreakpoint: 0.95,

      cssClass: 'description-modal',
    });

    return await modal.present();
  }

  async openForwardAttendeeModal(eventId: string) {
    const modal = await this.modalController.create({
      component: AddAttendeeComponent,
      initialBreakpoint: 0.95,
      breakpoints: [0, 0.95],
      componentProps: {
        initialAttendees: this.calendarEvent().attendees,
        startTime: this.calendarEvent().startTime,
        endTime: this.calendarEvent().endTime,
        purpose: 'forward',
      },
    });

    modal.onDidDismiss().then((result) => {
      if (result.data) {
        let alreadyAddedUPNs = this.calendarEvent().attendees.map(attendee => attendee.userPrincipalName.toLowerCase());
        let toBeAddedUPNs = result.data.filter((attendee) => alreadyAddedUPNs == null || !alreadyAddedUPNs?.includes(attendee.userPrincipalName.toLowerCase()))
        const forwardRequest: ForwardEventRequest = {
          eventId: eventId,
          comment: '',
          toRecipients: toBeAddedUPNs.map((attendee) => attendee.userPrincipalName),
        };

        this.bookingsStore.forwardBooking(forwardRequest, 'particpant').subscribe({
          error: (err) => {
            this.toastService.openErrorToastAsync(this.translateService.instant('inviteAttendeeError'));
          },
        })
      }
    });

    await modal.present();
  }

  async openAddResourceModal(eventId: string) {
    const modal = await this.modalController.create({
      component: AddResourceComponent,
      initialBreakpoint: 0.95,
      breakpoints: [0, 0.95],
      componentProps: {
        initialResources: this.calendarEvent().resources || [],
        startTime: this.calendarEvent().startTime,
        endTime: this.calendarEvent().endTime,
      },
    });

    modal.onDidDismiss().then((result) => {
      if (result.data) {
        let alreadyAddedUPNs = this.calendarEvent().attendees.map(attendee => attendee.userPrincipalName.toLowerCase());
        let toBeAddedUPNs = result.data.filter((resource) => alreadyAddedUPNs == null || !alreadyAddedUPNs?.includes(resource.emailAddress.toLowerCase()))
        const forwardRequest: ForwardEventRequest = {
          eventId: eventId,
          comment: '',
          toRecipients: toBeAddedUPNs.map((resource) => resource.emailAddress),
        };

        this.bookingsStore.forwardBooking(forwardRequest, 'resource').subscribe({
            error: (err) => {
            this.toastService.openErrorToastAsync(this.translateService.instant('inviteAttendeeError'));
          },
        })
      }
    });

    await modal.present();
  }
  checkIn() {
    this.calendarEvent().isLoading = true;
    let eventRequest = this.mapCalendarEventIntoEventRequest();
    
    this.apiService.checkIn(this.calendarEvent().id, eventRequest).pipe(
      catchError((error) => {
        this.toastService.openErrorToastAsync("checkInError");
        return of(false);
      })
    ).subscribe((res)=>{
      this.calendarEvent().isLoading = false;
      if(res){
        this.bookingsStore.checkInBookingLocal(this.calendarEvent());
      }
    })
  }

  checkOut() {
    this.calendarEvent().isLoading = true;
    const eventRequest = this.mapCalendarEventIntoEventRequest();
    this.apiService.checkOut(this.calendarEvent().id, eventRequest).pipe(
      catchError((error) => {
        this.toastService.openErrorToastAsync("checkOutError");
        return of(false);
      })
    ).subscribe((res)=>{
      this.isCheckedOut = res;
      this.calendarEvent().isLoading = false;
    })
  }
  get isReadOnly() { return this.interactionMode === 'readOnly'; }
}
